import { useState, useMemo, useEffect } from 'react'
import { authedFetch } from '../api'
import { Link } from 'react-router-dom'
import type { RunResult, ResultLog, ResultEvent, RunAgent } from '../slices/runDetailsSlice'
import { fetchResultLogs, fetchRunDetails } from '../slices/runDetailsSlice'
import { useAppDispatch, useAppSelector } from '../hooks'
import './DetailedResultsTable.css'

interface DetailedResultsTableProps {
  results: RunResult[]
  runId: string
  runStatus: string
  agents?: RunAgent[]
  attemptsCount?: number
}

function StatusPassIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--color-pass)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6" />
      <path d="M5.5 8l2 2 3.5-4" />
    </svg>
  )
}

function StatusFailIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--color-fail)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6" />
      <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" />
    </svg>
  )
}

function WarningIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--color-partial)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1.5l6.5 12H1.5L8 1.5z" />
      <path d="M8 6v3M8 11.5v.5" />
    </svg>
  )
}

function SpinnerIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--color-text-muted)" strokeWidth="2" strokeLinecap="round" className="result-spinner">
      <path d="M8 2a6 6 0 1 1-6 6" />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
  )
}

function JudgeDropdown({ label, verdict, reasoning }: { label: string; verdict: string; reasoning: string }) {
  const [expanded, setExpanded] = useState(false)
  if (!verdict && !reasoning) return null

  const verdictUpper = verdict.toUpperCase()
  const isPass = verdict === 'not_dsq' || verdict === 'pass'
  const isFail = verdict === 'dsq' || verdict === 'fail'

  return (
    <div className="judge-dropdown">
      <button className="judge-dropdown-toggle" onClick={() => setExpanded(!expanded)}>
        <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease', flexShrink: 0 }}>
          <path d="M6 4l4 4-4 4" />
        </svg>
        <span className={`judge-verdict-badge ${isPass ? 'judge-pass' : isFail ? 'judge-fail' : 'judge-unknown'}`}>
          {verdictUpper || '?'}
        </span>
        <span className="judge-label">{label}</span>
      </button>
      {expanded && reasoning && (
        <pre className="judge-reasoning">{reasoning}</pre>
      )}
    </div>
  )
}

function JudgeAssessment({ result }: { result: RunResult }) {
  const hasDsq = !!(result.dsq_claude_result || result.dsq_codex_result)
  const hasGrade = !!(result.grade_claude_result || result.grade_codex_result)
  const hasRootCauseJudge = !!result.root_cause_judge_result

  if (!hasDsq && !hasGrade && !hasRootCauseJudge) {
    // Fallback to raw llm_assessment text
    if (!result.llm_assessment) return null
    return (
      <pre className="judge-reasoning">{result.llm_assessment}</pre>
    )
  }

  return (
    <div className="judge-assessment">
      {hasDsq && (
        <div className="judge-section">
          <div className="judge-section-title">Disqualification Check</div>
          <JudgeDropdown label="Claude" verdict={result.dsq_claude_result} reasoning={result.dsq_claude} />
          <JudgeDropdown label="Codex" verdict={result.dsq_codex_result} reasoning={result.dsq_codex} />
        </div>
      )}
      {(hasGrade || hasRootCauseJudge) && (
        <div className="judge-section">
          <div className="judge-section-title">Grade</div>
          <JudgeDropdown label="Claude" verdict={result.grade_claude_result} reasoning={result.grade_claude} />
          <JudgeDropdown label="Codex" verdict={result.grade_codex_result} reasoning={result.grade_codex} />
          {hasRootCauseJudge && (
            <JudgeDropdown label="Root Cause" verdict={result.root_cause_judge_result} reasoning={result.root_cause_judge} />
          )}
        </div>
      )}
    </div>
  )
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }}>
      <path d="M6 4l4 4-4 4" />
    </svg>
  )
}

function getStatusIcon(status: string) {
  if (status === 'pass') return <StatusPassIcon />
  if (status === 'fail') return <StatusFailIcon />
  if (status === 'dsq' || status === 'disagree') return <WarningIcon />
  if (status === 'not_started') return null
  if (status === 'fix_completed') return <StatusPassIcon />
  return <SpinnerIcon />
}

function getStatusLabel(status: string): string {
  if (status === 'not_started') return 'NOT STARTED'
  if (status === 'pending') return 'FIXING'
  if (status === 'fix_completed') return 'FIX COMPLETED'
  if (status === 'dsq') return 'DSQ'
  return status.toUpperCase()
}

function JudgeBadges({ result }: { result: RunResult }) {
  if (result.status !== 'disagree' && result.status !== 'dsq') return null

  // For disagree (always grade disagree), show grade/rca judge verdicts
  // For DSQ, show DSQ verdicts from both judges
  const verdicts: Array<{ verdict: string; title: string }> = []
  if (result.status === 'dsq') {
    if (result.dsq_claude_result) verdicts.push({ verdict: result.dsq_claude_result, title: `Claude: ${result.dsq_claude_result}` })
    if (result.dsq_codex_result) verdicts.push({ verdict: result.dsq_codex_result, title: `Gemini: ${result.dsq_codex_result}` })
  } else {
    if (result.grade_claude_result) verdicts.push({ verdict: result.grade_claude_result, title: `Claude: ${result.grade_claude_result}` })
    if (result.grade_codex_result) verdicts.push({ verdict: result.grade_codex_result, title: `Gemini: ${result.grade_codex_result}` })
    if (result.root_cause_judge_result) verdicts.push({ verdict: result.root_cause_judge_result, title: `RCA: ${result.root_cause_judge_result}` })
  }

  if (verdicts.length === 0) return null

  return (
    <span className="judge-mini-badges">
      {verdicts.map((v, i) => {
        const isPass = v.verdict === 'not_dsq' || v.verdict === 'pass'
        const isFail = v.verdict === 'dsq' || v.verdict === 'fail'
        return (
          <span key={i} className={`judge-mini-dot ${isPass ? 'judge-pass' : isFail ? 'judge-fail' : 'judge-unknown'}`} title={v.title} />
        )
      })}
    </span>
  )
}

function getStatusClass(status: string): string {
  if (status === 'pending') return 'fixing'
  return status
}


function formatLogTimestamp(ts: string): string {
  const d = new Date(ts)
  return d.toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

function formatShortTime(ts: string): string {
  const d = new Date(ts)
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

function formatEventDescription(evt: ResultEvent): string {
  if (evt.type === 'task.started') {
    return evt.phase === 'fixing' ? 'Fix task started' : 'Verify task started'
  }
  if (evt.type === 'task.done') {
    const data = evt.data || {}
    const cost = typeof data.cost === 'number' ? `$${data.cost.toFixed(2)}` : ''
    const duration = typeof data.duration_ms === 'number' ? `${Math.round(data.duration_ms / 1000)}s` : ''
    const failed = data.failed ? ' (failed)' : ''
    const details = [cost, duration].filter(Boolean).join(', ')
    const label = evt.phase === 'fixing' ? 'Fix task completed' : 'Verify task completed'
    return `${label}${failed}${details ? ` — ${details}` : ''}`
  }
  return evt.type
}

function PhaseLogs({ logs, events, phase, label }: { logs: ResultLog[]; events: ResultEvent[]; phase: string; label: string }) {
  const phaseLogs = logs.filter(l => l.phase === phase)
  const phaseEvents = events.filter(e => e.phase === phase)
  if (phaseLogs.length === 0 && phaseEvents.length === 0) return null

  return (
    <div className="result-phase-section">
      <div className="result-phase-header">{label}</div>
      {phaseEvents.map((evt) => (
        <div key={evt.id} className={`result-event-entry result-event-${evt.type.replace('.', '-')}`}>
          <span className="result-log-timestamp">{formatShortTime(evt.timestamp)}</span>
          <span className="result-event-label">{formatEventDescription(evt)}</span>
        </div>
      ))}
      {phaseLogs.map((log) => (
        <div key={log.id} className="result-log-entry">
          <span className="result-log-timestamp">{formatLogTimestamp(log.timestamp)}</span>
          <span className="result-log-line">{log.line}</span>
        </div>
      ))}
    </div>
  )
}

export function ResultRow({ result, runId, runStatus, showAgent, showAttempt, colSpan, containerIndex, onRefresh }: {
  result: RunResult; runId: string; runStatus: string; showAgent: boolean; showAttempt: boolean; colSpan: number; containerIndex?: number; onRefresh?: () => void
}) {
  const dispatch = useAppDispatch()
  const taskLogsEntry = useAppSelector((state) => state.runDetails.taskLogs[result.task_id])
  const [logsExpanded, setLogsExpanded] = useState(false)
  const [logsLoading, setLogsLoading] = useState(false)
  const [reassessing, setReassessing] = useState(false)
  const [retriggering, setRetriggering] = useState(false)
  // Use logs from the result object (run details page) or from the taskLogs store (failure detail page)
  const effectiveLogs = (result.logs && result.logs.length > 0) ? result.logs : (taskLogsEntry?.logs || [])
  const effectiveEvents = (result.events && result.events.length > 0) ? result.events : (taskLogsEntry?.events || [])
  const hasLogs = result.log_count > 0 || effectiveLogs.length > 0
  const logsLoaded = effectiveLogs.length > 0
  const isInProgress = result.status === 'fixing' || result.status === 'validating' || result.status === 'pending' || result.status === 'fix_completed' || result.status === 'not_started'

  const handleToggleLogs = async () => {
    if (!logsExpanded && hasLogs && !logsLoaded) {
      setLogsLoading(true)
      await dispatch(fetchResultLogs({ runId, taskId: result.task_id }))
      setLogsLoading(false)
    }
    setLogsExpanded(!logsExpanded)
  }

  const isFinished = result.status === 'pass' || result.status === 'fail' || result.status === 'dsq' || result.status === 'disagree'

  const handleRerunAssessment = async () => {
    setReassessing(true)
    try {
      await authedFetch('/api/rerun-assessments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_ids: [result.task_id] }),
      })
      await dispatch(fetchRunDetails(runId))
      onRefresh?.()
    } finally {
      setReassessing(false)
    }
  }

  const handleRetriggerTask = async () => {
    setRetriggering(true)
    try {
      await authedFetch('/api/retrigger-task', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task_id: result.task_id }),
      })
      await dispatch(fetchRunDetails(runId))
      onRefresh?.()
    } finally {
      setRetriggering(false)
    }
  }

  // Auto-refresh logs while expanded and run is in progress or result is validating
  useEffect(() => {
    if (!logsExpanded) return
    const isRunActive = runStatus === 'running' || runStatus === 'pending'
    if (!isRunActive && result.status !== 'validating') return
    const interval = setInterval(() => {
      dispatch(fetchResultLogs({ runId, taskId: result.task_id }))
    }, 3000)
    return () => clearInterval(interval)
  }, [logsExpanded, runStatus, result.status, dispatch, runId, result.task_id])

  const statusClass = getStatusClass(result.status)

  return (
    <>
      <tr data-testid={`result-row-${result.task_id}`} className={isInProgress ? 'result-row-pending' : ''}>
        <td>
          <div className="result-test-cell">
            <button
              className="result-logs-toggle"
              data-testid={`result-logs-toggle-${result.task_id}`}
              onClick={handleToggleLogs}
              aria-label={logsExpanded ? 'Hide logs' : 'Show logs'}
            >
              <ChevronIcon expanded={logsExpanded} />
            </button>
            <Link to={`/failures/${result.failure_id}`} className="result-test-link" title={result.failure_title || result.failure_id}>{result.failure_short_id || result.failure_title || result.failure_id}</Link>
          </div>
        </td>
        {showAgent && (
          <td>
            <span className="result-agent-badge" data-testid={`result-agent-${result.task_id}`}>
              {result.agent_name || '—'}
            </span>
          </td>
        )}
        {showAttempt && (
          <td data-testid={`result-attempt-${result.task_id}`}>
            #{result.attempt || 1}
          </td>
        )}
        <td>
          <span
            className={`result-status-badge ${statusClass}`}
            data-testid={`result-status-${result.task_id}`}
          >
            {getStatusIcon(result.status)} {getStatusLabel(result.status)}
          </span>
          <JudgeBadges result={result} />
          {isInProgress && (result.status === 'validating' ? result.verify_started_at : result.fix_started_at) && (
            <div className="result-status-time">
              {formatShortTime(result.status === 'validating' ? result.verify_started_at! : result.fix_started_at!)}
            </div>
          )}
        </td>
        <td>{isInProgress ? '-' : `$${result.cost.toFixed(2)}`}</td>
        <td>{isInProgress ? '-' : `${Math.round(result.time)}s`}</td>
      </tr>
      {logsExpanded && (
        <tr className="result-logs-row" data-testid={`result-logs-${result.task_id}`}>
          <td colSpan={colSpan}>
            <div className="result-logs-content">
              <div className="result-expanded-assessment" data-testid={`result-assessment-${result.task_id}`}>
                <div className="result-assessment-header">
                  <span className="result-expanded-assessment-label">Assessment:</span>
                  <span className={`result-status-badge ${getStatusClass(result.status)}`}>
                    {getStatusIcon(result.status)} {getStatusLabel(result.status)}
                  </span>
                  {result.assessed_at && (
                    <span className="result-assessed-at" data-testid={`result-assessed-at-${result.task_id}`}>
                      {formatLogTimestamp(result.assessed_at)}
                    </span>
                  )}
                  {isFinished && (
                    <>
                      <button
                        className="result-rerun-assessment-btn"
                        data-testid={`result-rerun-assessment-${result.task_id}`}
                        onClick={handleRerunAssessment}
                        disabled={reassessing}
                      >
                        {reassessing ? 'Reassessing...' : 'Rerun Assessment'}
                      </button>
                      <button
                        className="result-retrigger-btn"
                        data-testid={`result-retrigger-${result.task_id}`}
                        onClick={handleRetriggerTask}
                        disabled={retriggering}
                      >
                        {retriggering ? 'Retriggering...' : 'Retrigger Task'}
                      </button>
                    </>
                  )}
                </div>
                <JudgeAssessment result={result} />
                {result.container_name && containerIndex != null && (
                  <div className="result-container-info" data-testid={`result-container-${result.task_id}`}>
                    <span className="result-container-label">Container:</span>
                    <span className="result-container-name">#{containerIndex}</span>
                  </div>
                )}
              </div>
              {logsLoading && <div className="result-logs-loading">Loading logs...</div>}
              {!logsLoading && (
                <>
                  <PhaseLogs
                    logs={effectiveLogs}
                    events={effectiveEvents}
                    phase="fixing"
                    label="Fixing"
                  />
                  <PhaseLogs
                    logs={effectiveLogs}
                    events={effectiveEvents}
                    phase="validating"
                    label="Validating"
                  />
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function DetailedResultsTable({ results, runId, runStatus, agents, attemptsCount }: DetailedResultsTableProps) {
  const [filter, setFilter] = useState('')
  const [agentFilter, setAgentFilter] = useState<string>('all')
  const [agentFilterOpen, setAgentFilterOpen] = useState(false)

  const showAgent = (agents && agents.length > 1) || results.some(r => r.agent_name && r.agent_name !== results[0]?.agent_name)
  const showAttempt = (attemptsCount && attemptsCount > 1) || results.some(r => r.attempt > 1)

  const containerNameToIndex = useMemo(() => {
    const uniqueNames = [...new Set(results.filter(r => r.container_name).map(r => r.container_name!))]
    const map: Record<string, number> = {}
    uniqueNames.forEach((name, i) => { map[name] = i + 1 })
    return map
  }, [results])

  const filteredResults = useMemo(() => {
    let filtered = results
    if (agentFilter !== 'all') {
      filtered = filtered.filter(r => r.agent_id === agentFilter)
    }
    if (!filter) return filtered
    const lower = filter.toLowerCase()
    return filtered.filter(
      (r) =>
        r.failure_id.toLowerCase().includes(lower) ||
        (r.failure_title && r.failure_title.toLowerCase().includes(lower)) ||
        (r.failure_short_id && r.failure_short_id.toLowerCase().includes(lower)) ||
        r.status.toLowerCase().includes(lower) ||
        r.llm_assessment.toLowerCase().includes(lower) ||
        (r.agent_name && r.agent_name.toLowerCase().includes(lower)) ||
        (r.container_name && r.container_name.toLowerCase().includes(lower))
    )
  }, [results, filter, agentFilter])

  const colSpan = 4 + (showAgent ? 1 : 0) + (showAttempt ? 1 : 0)

  if (results.length === 0) {
    return (
      <div data-testid="detailed-results-table" className="detailed-results">
        <div className="detailed-results-header">
          <span className="detailed-results-title">Detailed Results</span>
        </div>
        <div className="detailed-results-empty">No results available for this run.</div>
      </div>
    )
  }

  return (
    <div data-testid="detailed-results-table" className="detailed-results">
      <div className="detailed-results-header">
        <span className="detailed-results-title">Detailed Results</span>
        <div className="detailed-results-controls">
          {showAgent && agents && agents.length > 1 && (
            <div className="detailed-results-agent-filter" data-testid="agent-filter">
              <button
                type="button"
                className="detailed-results-agent-filter-btn"
                data-testid="agent-filter-btn"
                onClick={() => setAgentFilterOpen(!agentFilterOpen)}
              >
                {agentFilter === 'all' ? 'All Agents' : agents.find(a => a.agent_id === agentFilter)?.agent_name || 'Agent'}
                <span className="select-chevron">{agentFilterOpen ? '▲' : '▼'}</span>
              </button>
              {agentFilterOpen && (
                <div className="detailed-results-agent-dropdown">
                  <button
                    type="button"
                    className={`custom-dropdown-item ${agentFilter === 'all' ? 'selected' : ''}`}
                    onClick={() => { setAgentFilter('all'); setAgentFilterOpen(false) }}
                  >
                    All Agents
                  </button>
                  {agents.map(agent => (
                    <button
                      key={agent.agent_id}
                      type="button"
                      className={`custom-dropdown-item ${agentFilter === agent.agent_id ? 'selected' : ''}`}
                      data-testid={`agent-filter-option-${agent.agent_id}`}
                      onClick={() => { setAgentFilter(agent.agent_id); setAgentFilterOpen(false) }}
                    >
                      {agent.agent_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <span className="detailed-results-filter-icon"><SearchIcon /></span>
          <input
            data-testid="results-filter-input"
            className="detailed-results-filter"
            type="text"
            placeholder="Filter results..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      <div className="detailed-results-table-wrapper">
        <table className="detailed-results-table">
          <thead>
            <tr>
              <th>Test</th>
              {showAgent && <th>Agent</th>}
              {showAttempt && <th>Attempt</th>}
              <th>Result</th>
              <th>Cost</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.map((result) => (
              <ResultRow
                key={result.task_id}
                result={result}
                runId={runId}
                runStatus={runStatus}
                showAgent={showAgent}
                showAttempt={showAttempt}
                colSpan={colSpan}
                containerIndex={result.container_name ? containerNameToIndex[result.container_name] : undefined}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DetailedResultsTable
